import React, { useState, useEffect } from 'react';
import { Search, Eye, MessageSquare, Clock, CheckCircle, AlertCircle, Send, X, FileText, Image, Download, Paperclip, Trash2 } from 'lucide-react';
import api from '@/api/api';

const TicketManagement = () => {
  const [tickets, setTickets] = useState([]);
  const [stats, setStats] = useState({ total: 0, open: 0, in_progress: 0, resolved: 0, urgent_pending: 0 });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPriority, setFilterPriority] = useState('all');
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [response, setResponse] = useState('');
  const [newStatus, setNewStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [replyAttachments, setReplyAttachments] = useState([]);

  const MAX_ATTACHMENTS = 3;
  const MAX_FILE_SIZE_KB = 200;
  const ALLOWED_TYPES = {
    'image/jpeg': '.jpg',
    'image/png': '.png',
    'image/webp': '.webp',
    'application/pdf': '.pdf',
    'application/msword': '.doc',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': '.docx',
    'application/vnd.ms-excel': '.xls',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': '.xlsx'
  };

  const resizeImage = (file, maxSizeKB) => {
    return new Promise((resolve) => {
      if (!file.type.startsWith('image/')) {
        const reader = new FileReader();
        reader.onload = () => resolve({ data: reader.result, size: file.size });
        reader.readAsDataURL(file);
        return;
      }
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let { width, height } = img;
          let quality = 0.8;
          const maxBytes = maxSizeKB * 1024;
          
          // Scale down if very large
          const maxDim = 1200;
          if (width > maxDim || height > maxDim) {
            const ratio = Math.min(maxDim / width, maxDim / height);
            width = Math.round(width * ratio);
            height = Math.round(height * ratio);
          }
          
          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);
          
          // Iteratively reduce quality until under maxSizeKB
          let dataUrl = canvas.toDataURL('image/jpeg', quality);
          while (dataUrl.length * 0.75 > maxBytes && quality > 0.1) {
            quality -= 0.1;
            dataUrl = canvas.toDataURL('image/jpeg', quality);
          }
          // If still too large, reduce dimensions further
          if (dataUrl.length * 0.75 > maxBytes) {
            const ratio = Math.sqrt(maxBytes / (dataUrl.length * 0.75));
            canvas.width = Math.round(width * ratio);
            canvas.height = Math.round(height * ratio);
            ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
            dataUrl = canvas.toDataURL('image/jpeg', 0.7);
          }
          resolve({ data: dataUrl, size: Math.round(dataUrl.length * 0.75) });
        };
        img.src = e.target.result;
      };
      reader.readAsDataURL(file);
    });
  };

  const handleAttachmentUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (replyAttachments.length + files.length > MAX_ATTACHMENTS) {
      alert(`Maximum ${MAX_ATTACHMENTS} attachments allowed`);
      e.target.value = '';
      return;
    }
    
    for (const file of files) {
      if (!ALLOWED_TYPES[file.type]) {
        alert(`${file.name}: File type not supported. Allowed: JPG, PNG, WebP, PDF, Word, Excel`);
        continue;
      }
      
      const result = await resizeImage(file, MAX_FILE_SIZE_KB);
      setReplyAttachments(prev => [...prev, {
        filename: file.name,
        data: result.data,
        size: result.size,
        type: file.type,
        originalSize: file.size
      }]);
    }
    e.target.value = '';
  };

  const removeAttachment = (index) => {
    setReplyAttachments(prev => prev.filter((_, i) => i !== index));
  };

  useEffect(() => {
    fetchTickets();
    fetchStats();
  }, []);

  const fetchTickets = async () => {
    setLoading(true);
    try {
      const response = await api.get('/tickets/all');
      setTickets(response.data);
    } catch (error) {
      console.error('Error fetching tickets:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      const response = await api.get('/tickets/stats');
      setStats(response.data);
    } catch (error) {
      console.error('Error fetching stats:', error);
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'open': return 'bg-yellow-100 text-yellow-800';
      case 'in_progress': return 'bg-blue-100 text-blue-800';
      case 'resolved': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent': return 'bg-red-100 text-red-800 border-red-300';
      case 'high': return 'bg-orange-100 text-orange-800';
      case 'medium': return 'bg-yellow-100 text-yellow-800';
      case 'low': return 'bg-green-100 text-green-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const filteredTickets = tickets.filter(ticket => {
    const matchesSearch = 
      ticket.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.ticket_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      ticket.tenant_name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = filterStatus === 'all' || ticket.status === filterStatus;
    const matchesPriority = filterPriority === 'all' || ticket.priority === filterPriority;
    return matchesSearch && matchesStatus && matchesPriority;
  });

  const handleViewTicket = (ticket) => {
    setSelectedTicket(ticket);
    setNewStatus(ticket.status);
    setResponse('');
    setShowDetailModal(true);
  };

  const handleDeleteReply = async (ticketId) => {
    if (!window.confirm('Are you sure you want to delete this reply?')) return;
    try {
      await api.delete(`/tickets/${ticketId}/reply`);
      alert('Reply deleted successfully!');
      setShowDetailModal(false);
      await fetchTickets();
      await fetchStats();
    } catch (error) {
      console.error('Error deleting reply:', error);
      alert('Failed to delete reply.');
    }
  };


  const handleSubmitResponse = async () => {
    if (!response.trim() && newStatus === selectedTicket.status) {
      alert('Please enter a response or change the status');
      return;
    }

    setSubmitting(true);
    try {
      // Build update payload - always include status if response is provided
      const updatePayload = {};
      
      // Include status update
      if (newStatus !== selectedTicket.status) {
        updatePayload.status = newStatus;
      }
      
      // Include response if provided
      if (response.trim()) {
        updatePayload.admin_response = response.trim();
        // If response is given, also ensure status is included
        if (!updatePayload.status) {
          updatePayload.status = newStatus;
        }
      }
      
      // Include reply attachments
      if (replyAttachments.length > 0) {
        updatePayload.reply_attachments = replyAttachments.map(a => ({
          filename: a.filename,
          data: a.data,
          size: a.size,
          type: a.type
        }));
      }
      
      // Make single API call with all updates
      if (Object.keys(updatePayload).length > 0) {
        await api.put(`/tickets/${selectedTicket.id}`, updatePayload);
      }

      alert('Ticket updated successfully!');
      setShowDetailModal(false);
      setReplyAttachments([]);
      // Refresh data
      await fetchTickets();
      await fetchStats();
    } catch (error) {
      console.error('Error updating ticket:', error);
      alert('Failed to update ticket. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Ticket Management</h1>
        <p className="text-sm text-gray-500 mt-1">Manage support tickets from all tenants</p>
      </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Total</p>
              <p className="text-2xl font-bold text-gray-900">{stats.total}</p>
            </div>
            <MessageSquare className="h-8 w-8 text-gray-400" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-yellow-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Open</p>
              <p className="text-2xl font-bold text-yellow-600">{stats.open}</p>
            </div>
            <Clock className="h-8 w-8 text-yellow-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-blue-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">In Progress</p>
              <p className="text-2xl font-bold text-blue-600">{stats.in_progress}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-blue-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-green-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Resolved</p>
              <p className="text-2xl font-bold text-green-600">{stats.resolved}</p>
            </div>
            <CheckCircle className="h-8 w-8 text-green-500" />
          </div>
        </div>
        <div className="bg-white rounded-lg shadow-sm border border-red-200 p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-500">Urgent</p>
              <p className="text-2xl font-bold text-red-600">{stats.urgent_pending}</p>
            </div>
            <AlertCircle className="h-8 w-8 text-red-500" />
          </div>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
        <div className="flex flex-col md:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
            <input
              type="text"
              placeholder="Search by ticket #, title, or tenant..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <select
            value={filterStatus}
            onChange={(e) => setFilterStatus(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Status</option>
            <option value="open">Open</option>
            <option value="in_progress">In Progress</option>
            <option value="resolved">Resolved</option>
          </select>
          <select
            value={filterPriority}
            onChange={(e) => setFilterPriority(e.target.value)}
            className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
          >
            <option value="all">All Priority</option>
            <option value="urgent">Urgent</option>
            <option value="high">High</option>
            <option value="medium">Medium</option>
            <option value="low">Low</option>
          </select>
        </div>
      </div>

      {/* Tickets Table */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-x-auto">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Ticket #</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Title</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Priority</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Created</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">Loading...</td>
                </tr>
              ) : filteredTickets.length === 0 ? (
                <tr>
                  <td colSpan="8" className="px-4 py-8 text-center text-gray-500">
                    No tickets found.
                  </td>
                </tr>
              ) : (
                filteredTickets.map((ticket) => (
                  <tr key={ticket.id} className={`hover:bg-gray-50 ${ticket.priority === 'urgent' ? 'bg-red-50' : ''}`}>
                    <td className="px-4 py-3 text-sm font-medium text-blue-600">
                      <div className="flex items-center gap-1">
                        {ticket.ticket_number}
                        {ticket.attachments?.length > 0 && (
                          <Paperclip className="h-3 w-3 text-gray-400" title={`${ticket.attachments.length} attachment(s)`} />
                        )}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <p className="text-sm font-medium text-gray-900">{ticket.tenant_name}</p>
                      <p className="text-xs text-gray-500">{ticket.tenant_code}</p>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-900 max-w-xs truncate">{ticket.title}</td>
                    <td className="px-4 py-3 text-sm text-gray-600 capitalize">{ticket.category}</td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${getPriorityColor(ticket.priority)}`}>
                        {ticket.priority}
                      </span>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`px-2 py-1 text-xs rounded-full capitalize ${getStatusColor(ticket.status)}`}>
                        {ticket.status?.replace('_', ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {ticket.created_at?.split('T')[0]}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <button
                        onClick={() => handleViewTicket(ticket)}
                        className="text-blue-600 hover:text-blue-800"
                        title="View & Respond"
                      >
                        <Eye className="h-5 w-5" />
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Ticket Detail & Response Modal */}
      {showDetailModal && selectedTicket && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg p-6 w-full max-w-3xl max-h-[90vh] overflow-y-auto">
            <div className="flex justify-between items-start mb-4">
              <div>
                <h2 className="text-xl font-bold text-gray-900">{selectedTicket.title}</h2>
                <p className="text-sm text-gray-500">{selectedTicket.ticket_number}</p>
              </div>
              <button onClick={() => setShowDetailModal(false)} className="text-gray-500 hover:text-gray-700">
                <X className="h-5 w-5" />
              </button>
            </div>
            
            <div className="space-y-4">
              {/* Tenant & Status Info */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 p-4 rounded-lg">
                <div>
                  <p className="text-sm text-gray-500">Tenant</p>
                  <p className="font-medium">{selectedTicket.tenant_name}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Category</p>
                  <p className="font-medium capitalize">{selectedTicket.category}</p>
                </div>
                <div>
                  <p className="text-sm text-gray-500 mb-1">Priority</p>
                  <select
                    value={selectedTicket.priority || 'medium'}
                    onChange={async (e) => {
                      const newPriority = e.target.value;
                      try {
                        await api.put(`/tickets/${selectedTicket.id}`, { priority: newPriority });
                        setSelectedTicket({ ...selectedTicket, priority: newPriority });
                        await fetchTickets();
                        await fetchStats();
                      } catch (err) {
                        console.error('Error updating priority:', err);
                        alert('Failed to update priority');
                      }
                    }}
                    className={`px-3 py-1.5 text-sm rounded-lg border font-medium capitalize cursor-pointer ${getPriorityColor(selectedTicket.priority)}`}
                    data-testid="ticket-priority-select"
                  >
                    <option value="low">Low</option>
                    <option value="medium">Medium</option>
                    <option value="high">High</option>
                    <option value="urgent">Urgent</option>
                  </select>
                </div>
                <div>
                  <p className="text-sm text-gray-500">Created By</p>
                  <p className="font-medium">{selectedTicket.created_by_name}</p>
                </div>
              </div>

              {/* Description */}
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Description</h3>
                <p className="text-gray-700 whitespace-pre-wrap bg-gray-50 p-4 rounded-lg">
                  {selectedTicket.description}
                </p>
              </div>

              {/* Attachments */}
              {selectedTicket.attachments && selectedTicket.attachments.length > 0 && (
                <div>
                  <h3 className="font-semibold text-gray-900 mb-2 flex items-center gap-2">
                    <Paperclip className="h-4 w-4" />
                    Attachments ({selectedTicket.attachments.length})
                  </h3>
                  <div className="grid grid-cols-2 gap-3">
                    {selectedTicket.attachments.map((file, index) => (
                      <div key={index} className="border rounded-lg p-3 bg-gray-50">
                        <div className="flex items-center gap-2 mb-2">
                          {file.type === 'application/pdf' ? (
                            <FileText className="h-5 w-5 text-red-500" />
                          ) : (
                            <Image className="h-5 w-5 text-blue-500" />
                          )}
                          <span className="text-sm font-medium text-gray-700 truncate flex-1">
                            {file.filename}
                          </span>
                          <span className="text-xs text-gray-400">
                            {(file.size / 1024).toFixed(1)} KB
                          </span>
                        </div>
                        
                        {/* Preview for images */}
                        {file.type?.startsWith('image/') && (
                          <div className="mb-2">
                            <img 
                              src={file.data} 
                              alt={file.filename}
                              className="w-full h-24 object-cover rounded border"
                            />
                          </div>
                        )}
                        
                        {/* Download button */}
                        <a
                          href={file.data}
                          download={file.filename}
                          className="flex items-center justify-center gap-1 w-full px-3 py-1.5 text-sm bg-blue-600 text-white rounded hover:bg-blue-700"
                        >
                          <Download className="h-3 w-3" />
                          Download
                        </a>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Previous Response */}
              {selectedTicket.admin_response && (
                <div className="border-t pt-4">
                  <div className="flex items-center justify-between mb-2">
                    <h3 className="font-semibold text-gray-900">Previous Response</h3>
                    <button
                      onClick={() => handleDeleteReply(selectedTicket.id)}
                      className="flex items-center gap-1 px-3 py-1 text-xs bg-red-50 text-red-600 border border-red-200 rounded-lg hover:bg-red-100 transition-colors"
                      data-testid="delete-reply-btn"
                    >
                      <Trash2 className="h-3 w-3" />
                      Delete Reply
                    </button>
                  </div>
                  <div className="bg-blue-50 p-4 rounded-lg">
                    <p className="text-gray-700 whitespace-pre-wrap">{selectedTicket.admin_response}</p>
                    <p className="text-sm text-gray-500 mt-2">
                      By {selectedTicket.responded_by} • {selectedTicket.responded_at?.split('T')[0]}
                    </p>
                    {selectedTicket.reply_attachments?.length > 0 && (
                      <div className="mt-3 space-y-1">
                        <p className="text-xs font-medium text-gray-500">Attachments:</p>
                        {selectedTicket.reply_attachments.map((file, idx) => (
                          <a
                            key={idx}
                            href={file.data}
                            download={file.filename}
                            className="flex items-center gap-2 text-sm text-blue-600 hover:text-blue-800"
                          >
                            {file.type?.startsWith('image/') ? <Image className="h-3 w-3" /> : <FileText className="h-3 w-3" />}
                            {file.filename} ({(file.size / 1024).toFixed(0)}KB)
                          </a>
                        ))}
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Response Section */}
              <div className="border-t pt-4">
                <h3 className="font-semibold text-gray-900 mb-3">Respond to Ticket</h3>
                
                {/* Status Change */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Update Status</label>
                  <select
                    value={newStatus}
                    onChange={(e) => setNewStatus(e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  >
                    <option value="open">Open</option>
                    <option value="in_progress">In Progress</option>
                    <option value="resolved">Resolved</option>
                  </select>
                </div>

                {/* Response Text */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Response Message</label>
                  <textarea
                    value={response}
                    onChange={(e) => setResponse(e.target.value)}
                    placeholder="Type your response to the tenant..."
                    rows="4"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                {/* Attachment Upload */}
                <div className="mb-4">
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Attachments ({replyAttachments.length}/{MAX_ATTACHMENTS})
                  </label>
                  
                  {replyAttachments.length < MAX_ATTACHMENTS && (
                    <label className="flex items-center gap-2 px-4 py-2 border-2 border-dashed border-gray-300 rounded-lg cursor-pointer hover:border-blue-400 hover:bg-blue-50 transition-colors">
                      <Paperclip className="h-4 w-4 text-gray-500" />
                      <span className="text-sm text-gray-600">Add attachment (JPG, PNG, WebP, PDF, Word, Excel)</span>
                      <input
                        type="file"
                        multiple
                        accept=".jpg,.jpeg,.png,.webp,.pdf,.doc,.docx,.xls,.xlsx"
                        onChange={handleAttachmentUpload}
                        className="hidden"
                      />
                    </label>
                  )}
                  <p className="text-xs text-gray-400 mt-1">Images auto-resized to max 200KB. Max 3 files.</p>

                  {replyAttachments.length > 0 && (
                    <div className="mt-2 space-y-2">
                      {replyAttachments.map((file, index) => (
                        <div key={index} className="flex items-center gap-2 px-3 py-2 bg-gray-50 rounded-lg">
                          {file.type.startsWith('image/') ? (
                            <Image className="h-4 w-4 text-blue-500 flex-shrink-0" />
                          ) : (
                            <FileText className="h-4 w-4 text-red-500 flex-shrink-0" />
                          )}
                          <span className="text-sm text-gray-700 truncate flex-1">{file.filename}</span>
                          <span className="text-xs text-gray-400 flex-shrink-0">
                            {file.originalSize > file.size 
                              ? `${(file.originalSize / 1024).toFixed(0)}KB → ${(file.size / 1024).toFixed(0)}KB`
                              : `${(file.size / 1024).toFixed(0)}KB`
                            }
                          </span>
                          <button
                            type="button"
                            onClick={() => removeAttachment(index)}
                            className="p-1 text-red-500 hover:bg-red-50 rounded"
                          >
                            <X className="h-3 w-3" />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDetailModal(false)}
                    className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSubmitResponse}
                    disabled={submitting}
                    className="flex-1 flex items-center justify-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                  >
                    <Send className="h-4 w-4" />
                    {submitting ? 'Sending...' : 'Send Response'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketManagement;
